import { useEffect, useRef } from "react";
import { Form, message } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { GoogleLogin, type CredentialResponse } from "@react-oauth/google";
import dentallyLogo from "../../../assets/dentally-logo.png";
import CreateAccountInjectors from "./injectors";
import { selectSignup } from "./selector";
import { saveSignup } from "./slice";
import { selectGoogleLogin } from "../Login/selector";
import { saveGoogleLogin } from "../Login/slice";
import {
  stripLeadingWhitespace,
  stripWhitespace,
} from "../../../utils/formNormalizers";
import type { CreateAccountFormValues } from "./types";
import {
  BackButton,
  FormCard,
  GoogleLoginWrapper,
  ImageCard,
  LeftCol,
  LogoImage,
  OrDivider,
  PageRow,
  PrimaryButton,
  RightCol,
  StyledCheckbox,
  StyledForm,
  StyledInput,
  StyledPasswordInput,
  StyledTitle,
  SubmitFormItem,
  SubtitleLink,
  SubtitleRow,
  SubtitleText,
  TermsItem,
  TermsLink,
  TermsText,
} from "./style";

const { Item, useForm } = Form;

const PASSWORD_RULES_MESSAGE =
  "8-20 chars, with uppercase, lowercase, number, and special char (@$#!%*?&).";
const PASSWORD_REGEX =
  /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[#@$!%*?&])[A-Za-z\d@$#!%*?&]{8,20}$/;
const FULL_NAME_REGEX = /^[A-Za-z ]+$/;

export default function CreateAccountPage() {
  const [form] = useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { saving } = useSelector(selectSignup);
  const googleLogin = useSelector(selectGoogleLogin);
  const wasSavingGoogle = useRef(false);

  useEffect(() => {
    if (
      wasSavingGoogle.current &&
      !googleLogin.saving &&
      !googleLogin.error &&
      googleLogin.data?.token
    ) {
      message.success(`Welcome, ${googleLogin.data.full_name}`);
      navigate("/dashboard");
    }
    wasSavingGoogle.current = googleLogin.saving;
  }, [googleLogin, navigate]);

  function handleFinish(values: unknown) {
    const v = values as CreateAccountFormValues;
    dispatch(
      saveSignup({
        full_name: v.fullName.trim(),
        email: v.email.trim(),
        password: v.password,
        confirm_password: v.confirmPassword,
      }),
    );
  }

  function handleGoogleSuccess({ credential }: CredentialResponse) {
    if (!credential) {
      message.error("Google sign-in returned no credential.");
      return;
    }
    dispatch(saveGoogleLogin({ token: credential }));
  }

  function handleGoogleError() {
    message.error("Google sign-in was cancelled or failed.");
  }

  return (
    <PageRow>
      <CreateAccountInjectors />
      <LeftCol xs={0} sm={0} md={0} lg={12} xl={12}>
        <ImageCard />
      </LeftCol>

      <RightCol xs={24} sm={24} md={24} lg={12} xl={12}>
        <FormCard vertical align="flex-start">
          <LogoImage
            src={dentallyLogo}
            alt="Dentally"
            width={233}
            preview={false}
          />

          <BackButton
            aria-label="Back to login"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/login")}
          />

          <StyledTitle level={1}>Create an Account</StyledTitle>

          <SubtitleRow gap={5}>
            <SubtitleText>Already have an account?</SubtitleText>
            <SubtitleLink href="/login">Log in</SubtitleLink>
          </SubtitleRow>

          <StyledForm
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            requiredMark={false}
          >
            <Item
              name="fullName"
              label="Full Name"
              validateFirst
              normalize={stripLeadingWhitespace}
              rules={[
                { required: true, message: "Please enter your full name" },
                { min: 3, message: "Full name must be at least 3 characters" },
                { max: 55, message: "Full name must be 55 characters or less" },
                {
                  pattern: FULL_NAME_REGEX,
                  message: "Full name must contain only letters and spaces",
                },
              ]}
            >
              <StyledInput
                placeholder="Enter your full name"
                autoComplete="name"
              />
            </Item>

            <Item
              name="email"
              label="Email Address"
              validateFirst
              normalize={stripWhitespace}
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Enter a valid email" },
              ]}
            >
              <StyledInput
                placeholder="Enter your email address"
                autoComplete="email"
              />
            </Item>

            <Item
              name="password"
              label="Password"
              validateFirst
              normalize={stripWhitespace}
              rules={[
                { required: true, message: "Please enter your password" },
                { pattern: PASSWORD_REGEX, message: PASSWORD_RULES_MESSAGE },
              ]}
            >
              <StyledPasswordInput
                placeholder="Enter your password"
                autoComplete="new-password"
              />
            </Item>

            <Item
              name="confirmPassword"
              label="Confirm Password"
              dependencies={["password"]}
              validateFirst
              normalize={stripWhitespace}
              rules={[
                { required: true, message: "Please confirm your password" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Passwords do not match"));
                  },
                }),
              ]}
            >
              <StyledPasswordInput
                placeholder="Confirm your password"
                autoComplete="new-password"
              />
            </Item>

            <TermsItem
              name="acceptTerms"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(
                            "You must accept the Terms & Conditions to continue",
                          ),
                        ),
                },
              ]}
            >
              <StyledCheckbox>
                <TermsText>I agree to the</TermsText>
                <TermsLink
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/terms-and-conditions");
                  }}
                >
                  Terms &amp; Condition
                </TermsLink>
              </StyledCheckbox>
            </TermsItem>

            <SubmitFormItem>
              <PrimaryButton
                type="primary"
                htmlType="submit"
                block
                loading={saving}
              >
                Create Account
              </PrimaryButton>
            </SubmitFormItem>
          </StyledForm>

          <OrDivider>or</OrDivider>

          <GoogleLoginWrapper justify="center">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              useOneTap={false}
              theme="outline"
              size="large"
              text="signup_with"
              shape="pill"
              width="370"
            />
          </GoogleLoginWrapper>
        </FormCard>
      </RightCol>
    </PageRow>
  );
}
